import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refreshAccess } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = sp.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await client.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshAccess();
          setStatus("paid");
          setTimeout(() => nav("/"), 1800);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status) || attempts.current > 15) {
          setStatus("error");
          return;
        }
      } catch {
        if (attempts.current > 15) { setStatus("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4 text-white">
      <div className="w-full max-w-md border border-white/10 bg-[#111111] p-8 text-center sg-fade" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 size={34} className="mx-auto animate-spin text-[#00F0FF]" />
            <h1 className="mt-4 font-head text-xl font-bold">Confirming your subscription…</h1>
            <p className="mt-1 text-sm text-gray-500">Hang tight, verifying payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-[#00FF66]" />
            <h1 className="mt-4 font-head text-2xl font-black">Welcome to Pro</h1>
            <p className="mt-1 text-sm text-gray-400">Your $20/mo subscription is active. Redirecting to the scanner…</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={40} className="mx-auto text-[#FF3B30]" />
            <h1 className="mt-4 font-head text-xl font-bold">Couldn't confirm payment</h1>
            <p className="mt-1 text-sm text-gray-500">If you completed checkout, it may take a moment. Try refreshing your access.</p>
            <button onClick={() => nav("/paywall")} className="mt-5 rounded-sm border border-white/10 px-4 py-2 font-mono-sg text-xs text-gray-300 hover:border-white/20">Back to plans</button>
          </>
        )}
      </div>
    </div>
  );
}
